/**
 * BHG Local Photo Assets
 *
 * Each photo set is exported with pre-built srcSet and sizes strings so every
 * <img> automatically serves the right resolution for the device.
 *
 * Vite fingerprints these files at build time (content hash in the URL), so
 * browsers cache them indefinitely and only re-fetch when the file actually
 * changes — no manual cache-busting needed.
 *
 * Usage:
 *   import { photos } from '@assets/photos';
 *   <img {...photos.moms.img} alt="…" loading="lazy" />
 *   // or spread only what you need
 *   <img src={photos.moms.src} srcSet={photos.moms.srcSet} sizes={photos.moms.heroSizes} … />
 */

// ── Parachute — login / auth split panel ──────────────────────────────────────
import parachute480  from './parachute-480.webp';
import parachute800  from './parachute-800.webp';
import parachute1440 from './parachute-1440.webp';
import parachute1920 from './parachute-1920.webp';

// ── Moms and Toddlers — How It Works section ──────────────────────────────────
import momsAndToddlers480  from './moms-and-toddlers-480.webp';
import momsAndToddlers800  from './moms-and-toddlers-800.webp';
import momsAndToddlers1440 from './moms-and-toddlers-1440.webp';
import momsAndToddlers1920 from './moms-and-toddlers-1920.webp';

// ── Kids Bench — Our Story section ────────────────────────────────────────────
import kidsBench480  from './kids-bench-480.webp';
import kidsBench800  from './kids-bench-800.webp';
import kidsBench1440 from './kids-bench-1440.webp';
import kidsBench1920 from './kids-bench-1920.webp';

// ── Family Flower — Encourage & Support Families pillar ───────────────────────
import familyFlower480  from './family-flower-480.webp';
import familyFlower800  from './family-flower-800.webp';
import familyFlower1440 from './family-flower-1440.webp';
import familyFlower1920 from './family-flower-1920.webp';

// ── Outdoor Teacher + Students + Plants — Socialization pillar ────────────────
import outdoorTeacher480  from './outdoor-teacher-students-plants-480.webp';
import outdoorTeacher800  from './outdoor-teacher-students-plants-800.webp';
import outdoorTeacher1440 from './outdoor-teacher-students-plants-1440.webp';
import outdoorTeacher1920 from './outdoor-teacher-students-plants-1920.webp';

// ── Teens Work Together Around Table — Education pillar ───────────────────────
import teensWork480  from './teens-work-together-around-table-480.webp';
import teensWork800  from './teens-work-together-around-table-800.webp';
import teensWork1440 from './teens-work-together-around-table-1440.webp';
import teensWork1920 from './teens-work-together-around-table-1920.webp';

// ── Teacher with Children at Picnic Table — Landing hero ─────────────────────
import teacherPicnic480  from './teacher-with-children-picnic-table-480.webp';
import teacherPicnic800  from './teacher-with-children-picnic-table-800.webp';
import teacherPicnic1440 from './teacher-with-children-picnic-table-1440.webp';
import teacherPicnic1920 from './teacher-with-children-picnic-table-1920.webp';

// ── Types ─────────────────────────────────────────────────────────────────────

export interface PhotoSet {
  /** Largest/default src (for browsers that ignore srcSet) */
  src: string;
  /** srcSet string — browser picks the best fit by width */
  srcSet: string;
  /**
   * sizes hint for full-width hero images.
   * Tells the browser the image will fill the viewport on all breakpoints.
   */
  heroSizes: string;
  /**
   * sizes hint for content images that sit inside a text/image layout
   * (roughly half-width on desktop, full-width on mobile).
   */
  contentSizes: string;
  /**
   * sizes hint for card images (~⅓ width on desktop, full-width on mobile).
   */
  cardSizes: string;
}

// ── Helper ────────────────────────────────────────────────────────────────────

function makePhotoSet(
  w480: string,
  w800: string,
  w1440: string,
  w1920: string
): PhotoSet {
  return {
    src: w1920,
    srcSet: `${w480} 480w, ${w800} 800w, ${w1440} 1440w, ${w1920} 1920w`,
    heroSizes: '100vw',
    contentSizes: '(max-width: 767px) 100vw, (max-width: 1365px) 50vw, 700px',
    cardSizes: '(max-width: 767px) 100vw, (max-width: 1365px) 33vw, 450px',
  };
}

// ── Exports ───────────────────────────────────────────────────────────────────

export const photos = {
  /** Login / auth split panel background */
  parachute: makePhotoSet(parachute480, parachute800, parachute1440, parachute1920),

  /** How It Works section — image beside the semester info */
  momsAndToddlers: makePhotoSet(momsAndToddlers480, momsAndToddlers800, momsAndToddlers1440, momsAndToddlers1920),

  /** Our Story section — image beside the timeline */
  kidsBench: makePhotoSet(kidsBench480, kidsBench800, kidsBench1440, kidsBench1920),

  /** "Encourage and Support Families" pillar card */
  familyFlower: makePhotoSet(familyFlower480, familyFlower800, familyFlower1440, familyFlower1920),

  /** "Socialization" pillar card */
  outdoorTeacher: makePhotoSet(outdoorTeacher480, outdoorTeacher800, outdoorTeacher1440, outdoorTeacher1920),

  /** "Education" pillar card */
  teensWork: makePhotoSet(teensWork480, teensWork800, teensWork1440, teensWork1920),

  /** Landing page hero background */
  teacherPicnic: makePhotoSet(teacherPicnic480, teacherPicnic800, teacherPicnic1440, teacherPicnic1920),
} as const;
